import { apiFetch } from './client';
import type { MetricUnit } from '../components/formatters';

export type OkrStatus = 'draft' | 'submitted' | 'approved' | 'changes_requested';
export type OkrDirection = 'up' | 'down';

// How the server rolls a metric's monthly series into the one quarter figure the
// target is judged against. Derived from the metric, not stored on the OKR.
export type MetricAggregation = 'sum' | 'avg' | 'last';

export const AGGREGATION_LABEL: Record<MetricAggregation, string> = {
  sum: 'quarter total',
  avg: 'quarter average',
  last: 'latest month',
};

export type OkrTaskStatus =
  | 'not_started'
  | 'in_progress'
  | 'done'
  | 'failed'
  | 'delayed'
  | 'on_track'
  | 'at_risk';
export type TaskMonth = 1 | 2 | 3;

export interface OkrTask {
  id: string;
  name: string;
  description: string;
  notionUrl: string;
  monthDue: TaskMonth;
  status: OkrTaskStatus;
  linkedUserId: string | null;
  linkedUser: OkrOwner | null;
}

export interface OkrMetric {
  key: string;
  layer: string;
  area: string;
  subarea: string;
  metric: string;
  unit: MetricUnit;
  format: string;
  aggregation: MetricAggregation;
}

export interface OkrKeyResult {
  layer: string;
  area: string;
  subarea: string;
  metric: string;
  label: string;
  unit: MetricUnit;
  format: string;
  targetValue: number;
  baselineValue: number | null;
  direction: OkrDirection;
  tasks: OkrTask[];
  // computed by the server: the quarter's actuals rolled up per `aggregation`,
  // measured against the target
  aggregation: MetricAggregation;
  currentValue: number | null;
  currentMonth: string | null;
  currentMonths: string[];
  progressPct: number | null;
  // per-quarter-month performance (end-of-month actual vs. target), computed by the server
  monthly: { month: string; value: number | null; progressPct: number | null }[];
}

export interface Okr {
  id: string;
  ownerId: string;
  quarter: string;
  status: OkrStatus;
  keyResults: OkrKeyResult[];
  managerNote: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  reviewedById: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface OkrListItem {
  id: string;
  quarter: string;
  status: OkrStatus;
  updatedAt: string;
}

export interface OkrOwner {
  id: string;
  name: string;
  email: string;
  picture: string | null;
}

export interface TeamOkr {
  okr: Okr;
  owner: OkrOwner | null;
}

export interface TaskInput {
  id?: string;
  name: string;
  description?: string;
  notionUrl?: string;
  monthDue: TaskMonth;
  linkedUserId?: string | null;
}

export interface DirectoryUser {
  id: string;
  name: string;
  email: string;
  picture: string | null;
}

export interface LinkedTask {
  taskId: string;
  name: string;
  description: string;
  monthDue: TaskMonth;
  status: OkrTaskStatus;
  krLabel: string;
  owner: OkrOwner | null;
}

export interface KeyResultInput {
  layer: string;
  area: string;
  subarea: string;
  metric: string;
  targetValue: number;
  direction?: OkrDirection;
  tasks?: TaskInput[];
}

export type ReviewBody =
  | { action: 'approve'; keyResults?: KeyResultInput[] }
  | { action: 'request_changes'; note: string; keyResults?: KeyResultInput[] };

export function fetchOkrMetrics(signal?: AbortSignal) {
  return apiFetch<{ metrics: OkrMetric[] }>('/api/okrs/metrics', { signal });
}

export function fetchMyOkr(quarter?: string, signal?: AbortSignal) {
  const qs = quarter ? `?${new URLSearchParams({ quarter }).toString()}` : '';
  return apiFetch<{ okr: Okr | null; quarter: string }>(`/api/okrs/me${qs}`, { signal });
}

export function fetchMyOkrList(signal?: AbortSignal) {
  return apiFetch<{ okrs: OkrListItem[] }>('/api/okrs/me/list', { signal });
}

export function saveMyOkr(body: { quarter: string; keyResults: KeyResultInput[] }) {
  return apiFetch<{ okr: Okr }>('/api/okrs/me', { method: 'PUT', body });
}

export function submitMyOkr(quarter: string) {
  return apiFetch<{ okr: Okr }>('/api/okrs/me/submit', { method: 'POST', body: { quarter } });
}

export function fetchTeamOkrs(status?: string, signal?: AbortSignal) {
  const qs = status ? `?${new URLSearchParams({ status }).toString()}` : '';
  return apiFetch<{ okrs: TeamOkr[] }>(`/api/okrs/team${qs}`, { signal });
}

export function fetchAllOkrs(quarter?: string, signal?: AbortSignal) {
  const qs = quarter ? `?${new URLSearchParams({ quarter }).toString()}` : '';
  return apiFetch<{ quarter: string; okrs: TeamOkr[] }>(`/api/okrs/all${qs}`, { signal });
}

export function reviewOkr(id: string, body: ReviewBody) {
  return apiFetch<{ okr: Okr }>(`/api/okrs/${id}/review`, { method: 'PATCH', body });
}

export function resetOkr(id: string) {
  return apiFetch<{ okr: Okr }>(`/api/okrs/${id}/reset`, { method: 'POST' });
}

export function updateTaskStatus(body: { quarter: string; taskId: string; status: OkrTaskStatus }) {
  return apiFetch<{ okr: Okr }>('/api/okrs/me/tasks', { method: 'PATCH', body });
}

export function fetchDirectory(signal?: AbortSignal) {
  return apiFetch<{ users: DirectoryUser[] }>('/api/okrs/directory', { signal });
}

export function fetchLinkedTasks(quarter?: string, signal?: AbortSignal) {
  const qs = quarter ? `?${new URLSearchParams({ quarter }).toString()}` : '';
  return apiFetch<{ quarter: string; tasks: LinkedTask[] }>(`/api/okrs/linked${qs}`, { signal });
}

// "YYYY-Q[1-4]" helpers (mirrors the API quarter util).
export function currentQuarter(now: Date = new Date()): string {
  const year = now.getFullYear();
  const quarter = Math.floor(now.getMonth() / 3) + 1;
  return `${year}-Q${quarter}`;
}

export function isPastQuarter(quarter: string, now: Date = new Date()): boolean {
  return quarter < currentQuarter(now);
}

export function quarterLabel(quarter: string): string {
  const m = /^(\d{4})-Q([1-4])$/.exec(quarter);
  if (!m) return quarter;
  return `Q${m[2]} ${m[1]}`;
}

// A small list of selectable quarters: the four most recent plus the next one,
// so a user can plan ahead or review past commitments.
export function quarterOptions(now: Date = new Date()): string[] {
  const year = now.getFullYear();
  const q = Math.floor(now.getMonth() / 3) + 1;
  const out: string[] = [];
  for (let offset = 1; offset >= -4; offset--) {
    let yy = year;
    let qq = q + offset;
    while (qq > 4) { qq -= 4; yy += 1; }
    while (qq < 1) { qq += 4; yy -= 1; }
    out.push(`${yy}-Q${qq}`);
  }
  return out;
}
